import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCurrentPosition,
  checkGeolocationPermission,
  checkGeofence,
  watchPosition,
  clearWatch,
  isGeolocationSupported
} from '@/lib/geoUtils';

/**
 * Hook for managing geolocation and geofence monitoring
 * @param {Object} options
 * @param {Object} options.geofence - Geofence config {latitude, longitude, radiusMeters}
 * @param {boolean} options.enableBackgroundMonitoring - Enable continuous monitoring
 * @param {Function} options.onGeofenceExit - Callback when user exits geofence
 * @param {Function} options.onGeofenceEnter - Callback when user enters geofence
 * @param {Function} options.onPositionUpdate - Callback on every position update
 */
export function useGeolocation({
  geofence = null,
  enableBackgroundMonitoring = false,
  onGeofenceExit = null,
  onGeofenceEnter = null,
  onPositionUpdate = null
} = {}) {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState('prompt');
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [supported] = useState(isGeolocationSupported());

  const watchIdRef = useRef(null);
  const wasInsideRef = useRef(null);
  const callbacksRef = useRef({ onGeofenceExit, onGeofenceEnter, onPositionUpdate });

  // Keep callbacks ref updated
  useEffect(() => {
    callbacksRef.current = { onGeofenceExit, onGeofenceEnter, onPositionUpdate };
  }, [onGeofenceExit, onGeofenceEnter, onPositionUpdate]);

  // Check permission on mount and listen for changes
  useEffect(() => {
    checkGeolocationPermission().then(setPermission);

    // Set up permission change listener if supported
    let permissionStatus = null;

    const setupPermissionListener = async () => {
      if (!navigator.permissions) return;

      try {
        permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

        const handlePermissionChange = () => {
          setPermission(permissionStatus.state);
        };

        permissionStatus.addEventListener('change', handlePermissionChange);

        return () => {
          permissionStatus.removeEventListener('change', handlePermissionChange);
        };
      } catch {
        // Permissions API not fully supported
      }
    };

    const cleanupPromise = setupPermissionListener();

    return () => {
      cleanupPromise?.then(cleanup => cleanup?.());
    };
  }, []);

  // Process position update and check geofence
  const processPosition = useCallback(
    (newPosition) => {
      setPosition(newPosition);
      setError(null);

      // Call position update callback
      callbacksRef.current.onPositionUpdate?.(newPosition);

      // Check geofence if configured
      if (geofence?.latitude && geofence?.longitude && geofence?.radiusMeters) {
        const status = checkGeofence(newPosition, geofence);
        setGeofenceStatus(status);

        // Trigger callbacks for geofence crossing (only after initial position)
        if (wasInsideRef.current !== null) {
          if (wasInsideRef.current && !status.isWithin) {
            callbacksRef.current.onGeofenceExit?.({
              position: newPosition,
              distance: status.distance
            });
          } else if (!wasInsideRef.current && status.isWithin) {
            callbacksRef.current.onGeofenceEnter?.({
              position: newPosition,
              distance: status.distance
            });
          }
        }
        wasInsideRef.current = status.isWithin;
      }

      return newPosition;
    },
    [geofence]
  );

  // Get current position once
  const getPosition = useCallback(async () => {
    if (!supported) {
      setError('Geolocation is not supported by this browser');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const pos = await getCurrentPosition();
      const newPosition = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp
      };

      setPermission('granted');
      return processPosition(newPosition);
    } catch (err) {
      setError(err.message);
      if (err.message.includes('denied')) {
        setPermission('denied');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [supported, processPosition]);

  // Start continuous monitoring
  const startMonitoring = useCallback(() => {
    if (!supported) {
      setError('Geolocation is not supported');
      return;
    }

    // Clear any existing watch
    if (watchIdRef.current !== null) {
      clearWatch(watchIdRef.current);
    }

    setIsMonitoring(true);

    watchIdRef.current = watchPosition(
      (newPosition) => {
        setPermission('granted');
        processPosition(newPosition);
      },
      (err) => {
        setError(err.message);
        if (err.message.includes('denied')) {
          setPermission('denied');
        }
      }
    );
  }, [supported, processPosition]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    if (watchIdRef.current !== null) {
      clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsMonitoring(false);
  }, []);

  // Reset geofence tracking state
  const resetGeofenceState = useCallback(() => {
    wasInsideRef.current = null;
    setGeofenceStatus(null);
  }, []);

  // Auto-start background monitoring if enabled
  useEffect(() => {
    if (enableBackgroundMonitoring && permission === 'granted' && geofence) {
      startMonitoring();
    } else if (!enableBackgroundMonitoring && isMonitoring) {
      stopMonitoring();
    }

    return () => {
      if (watchIdRef.current !== null) {
        clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [
    enableBackgroundMonitoring,
    permission,
    geofence,
    startMonitoring,
    stopMonitoring,
    isMonitoring
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        clearWatch(watchIdRef.current);
      }
    };
  }, []);

  return {
    // State
    position,
    error,
    loading,
    permission,
    geofenceStatus,
    isMonitoring,
    supported,

    // Actions
    getPosition,
    startMonitoring,
    stopMonitoring,
    resetGeofenceState,

    // Convenience getters
    isInsideGeofence: geofenceStatus?.isWithin ?? null,
    distanceFromGeofence: geofenceStatus?.distance ?? null
  };
}

export default useGeolocation;
